import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useLenis } from "lenis/react";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";

const Header = () => {
  const { lang, setLang, t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const lenis = useLenis();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { offset: -80 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollTo(id), 500);
    } else {
      scrollTo(id);
    }
  };

  const navItems = [
    { label: t("nav.about"), action: () => goSection("approche") },
    { label: t("nav.services"), action: () => goSection("services") },
    { label: t("nav.shop"), to: "/shop" },
    { label: t("nav.booking"), to: "/booking" },
    { label: t("nav.blog"), to: "/blog" },
    { label: t("nav.members"), to: "/members" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled ? "bg-background/85 backdrop-blur-xl border-b border-border py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="font-serif text-2xl lg:text-3xl tracking-tightest text-primary font-semibold">
          Nordicvy
        </Link>

        <nav className="hidden lg:flex items-center gap-8 text-sm">
          {navItems.map((item, i) =>
            item.to ? (
              <Link key={i} to={item.to} data-testid={`nav-${item.to.slice(1)}`} className="link-underline text-foreground/80 hover:text-foreground transition-colors">
                {item.label}
              </Link>
            ) : (
              <button key={i} onClick={item.action} data-testid={`nav-section-${i}`} className="link-underline text-foreground/80 hover:text-foreground transition-colors">
                {item.label}
              </button>
            )
          )}
        </nav>

        <div className="flex items-center gap-3 lg:gap-5">
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "fr" ? "en" : "fr")}
            className="hidden sm:flex items-center gap-1.5 text-xs font-medium text-foreground/70 hover:text-primary transition-colors"
          >
            <Globe className="w-3.5 h-3.5" />
            <span className={lang === "fr" ? "text-primary font-semibold" : ""}>FR</span>
            <span className="text-border">/</span>
            <span className={lang === "en" ? "text-primary font-semibold" : ""}>EN</span>
          </button>

          {user && user !== false ? (
            <>
              {user.role === "admin" && (
                <Link data-testid="admin-link" to="/admin" className="hidden lg:block text-sm text-foreground/70 hover:text-primary transition-colors">Admin</Link>
              )}
              <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} className="hidden lg:block text-sm text-foreground/70 hover:text-primary transition-colors">
                {t("nav.logout")}
              </button>
            </>
          ) : (
            <Link data-testid="login-link" to="/login" className="hidden lg:block text-sm text-foreground/70 hover:text-primary transition-colors">
              {t("nav.login")}
            </Link>
          )}

          <button
            data-testid="book-cta-header"
            onClick={() => navigate("/booking")}
            className="hidden sm:block bg-primary text-primary-foreground text-sm px-5 py-2.5 hover:bg-primary/90 active:scale-95 transition-[transform,background-color] duration-200"
          >
            {t("hero.cta1")}
          </button>

          <button data-testid="mobile-menu-btn" className="lg:hidden text-foreground" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-background border-t border-border mt-3 px-6 py-6 flex flex-col gap-4">
          {navItems.map((item, i) =>
            item.to ? (
              <Link key={i} to={item.to} onClick={() => setOpen(false)} className="text-lg font-serif text-foreground">
                {item.label}
              </Link>
            ) : (
              <button key={i} onClick={item.action} className="text-lg font-serif text-foreground text-left">
                {item.label}
              </button>
            )
          )}
          <div className="flex items-center gap-4 pt-4 border-t border-border">
            <button onClick={() => setLang(lang === "fr" ? "en" : "fr")} className="text-sm text-primary font-medium">
              {lang === "fr" ? "English" : "Français"}
            </button>
            {user && user !== false ? (
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="text-sm text-foreground/70">{t("nav.logout")}</button>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="text-sm text-foreground/70">{t("nav.login")}</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
